import { find } from "linkifyjs";

const ALLOWED_PROTOCOLS = new Set(["http:", "https:", "mailto:"]);
const FIND_OPTIONS = { defaultProtocol: "https" };

/**
 * @typedef {Object} LinkifyTextToken
 * @property {"text"|"internal"|"external"|"email"} type
 * @property {string} value Display text
 * @property {string} [href] Absolute href for external/email links
 * @property {string} [to] In-app path for same-origin URLs
 */

/**
 * Parse an href and classify it, or return null if it is not safe to render as a link.
 * @param {string} href
 * @param {string} matchType linkifyjs match type (`url` or `email`)
 * @returns {{ kind: "internal"|"external"|"email", href: string, to?: string } | null}
 */
function classifyHref(href, matchType) {
  let url;
  try {
    url = new URL(href);
  } catch {
    return null;
  }

  if (!ALLOWED_PROTOCOLS.has(url.protocol)) {
    return null;
  }

  if (matchType === "email" || url.protocol === "mailto:") {
    return { kind: "email", href: url.href };
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return null;
  }

  if (typeof window !== "undefined" && url.origin === window.location.origin) {
    return {
      kind: "internal",
      href: url.href,
      to: `${url.pathname}${url.search}${url.hash}`,
    };
  }

  return { kind: "external", href: url.href };
}

/**
 * Split plain text into text and safe link tokens using linkifyjs.
 * Unsafe schemes (javascript:, data:, etc.) are left as plain text.
 *
 * @param {string} text
 * @returns {LinkifyTextToken[]}
 */
export function getSafeLinkTokens(text) {
  if (!text) {
    return [];
  }

  const matches = find(text, FIND_OPTIONS).filter(
    (match) => match.type === "url" || match.type === "email"
  );
  if (matches.length === 0) {
    return [{ type: "text", value: text }];
  }

  /** @type {LinkifyTextToken[]} */
  const tokens = [];
  let cursor = 0;

  matches.forEach((match) => {
    const start = match.start ?? text.indexOf(match.value, cursor);
    const end = match.end ?? start + match.value.length;

    if (start > cursor) {
      tokens.push({ type: "text", value: text.slice(cursor, start) });
    }

    const classified = classifyHref(match.href, match.type);
    if (!classified) {
      tokens.push({ type: "text", value: match.value });
    } else if (classified.kind === "internal") {
      tokens.push({
        type: "internal",
        value: match.value,
        href: classified.href,
        to: classified.to,
      });
    } else if (classified.kind === "email") {
      tokens.push({
        type: "email",
        value: match.value,
        href: classified.href,
      });
    } else {
      tokens.push({
        type: "external",
        value: match.value,
        href: classified.href,
      });
    }

    cursor = end;
  });

  if (cursor < text.length) {
    tokens.push({ type: "text", value: text.slice(cursor) });
  }

  return tokens;
}
